import { ArchBaseService } from "@/common/api.service";

ArchBaseService.init();

export const InternalUserService = {
  /**
   * Get list of internal users with pagination
   * @param {Object} params - Query parameters
   * @param {Number} params.page - Page number
   * @param {Number} params.size - Items per page
   * @param {String} params.sort - Sort direction (asc/desc)
   * @param {String} params.sortby - Field to sort by
   * @param {String} params.search - Search term
   * @param {String} params.filterby - Field to filter by
   * @param {String} params.filtervalue - Value to filter
   * @param {String} params.condition - Filter condition (==, >=, >, <, <=, like)
   * @returns {Promise}
   */
  getUsers(params) {
    return ArchBaseService.get("internal/user/list", params);
  },

  /**
   * Create a new internal user
   * @param {Object} userData - User data
   * @param {String} userData.email - User email (required)
   * @param {String} userData.firstname - First name (required)
   * @param {String} userData.lastname - Last name (required)
   * @param {String} userData.phonenumber - Phone number (required)
   * @param {String} userData.countryid - Country ID (required)
   * @param {String} userData.functionid - Function ID (required)
   * @param {String} userData.roleid - Role ID (required)
   * @param {String} userData.serviceid - Service ID (required)
   * @param {String} userData.accesstypeid - Access type ID (required)
   * @param {Boolean} userData.twofactorenabled - Two factor enabled (optional)
   * @returns {Promise}
   */
  createUser(userData) {
    return ArchBaseService.create("internal/user/", userData);
  },

  /**
   * Update whether an internal user can process HQ transactions
   * @param {String} userid - Internal user UUID
   * @param {Boolean} canProcessHqTx - Whether the user can process HQ transactions
   * @returns {Promise}
   */
  updateCanProcessHqTx(userid, canProcessHqTx) {
    return ArchBaseService.patch("internal/user/process-hq", {
      userid,
      can_process_hq_tx: canProcessHqTx,
    });
  },

  /**
   * Get a single internal user's editable record (IDs resolved for prefill)
   * @param {String} userid - Internal user UUID
   * @returns {Promise}
   */
  getUser(userid) {
    return ArchBaseService.get("internal/user", userid);
  },

  /**
   * Update an internal user's editable profile fields. Email is immutable.
   * @param {String} userid - Internal user UUID
   * @param {Object} userData - { firstname, lastname, phonenumber, countryid,
   *   roleid, functionid, accesstypeid, can_process_hq_tx }
   * @returns {Promise}
   */
  updateUser(userid, userData) {
    return ArchBaseService.put(`internal/user/${userid}`, userData);
  },

  /**
   * Change an internal user's status (also the lock/unlock path).
   * @param {String} userid - Internal user UUID
   * @param {Number} status - UserStatusEnum value (0,1,2,3,4)
   * @returns {Promise}
   */
  updateUserStatus(userid, status) {
    return ArchBaseService.patch("internal/user/status", { userid, status });
  },

  /**
   * Get list of roles
   * @returns {Promise}
   */
  getRoles() {
    return ArchBaseService.get("auth/access/roles");
  },

  /**
   * Get list of functions
   * @returns {Promise}
   */
  getFunctions() {
    return ArchBaseService.get("auth/access/functions");
  },

  /**
   * Get list of services
   * @returns {Promise}
   */
  getServices() {
    return ArchBaseService.get("auth/access/services");
  },

  /**
   * Get list of access types
   * @returns {Promise}
   */
  getAccessTypes() {
    return ArchBaseService.get("auth/access/types");
  },
};

export default InternalUserService;
